import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Select,
  InputGroup,
  InputRightElement,
  Icon,
  Checkbox,
  SimpleGrid,
  VStack,
  Box,
  keyframes,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { createAPIEndPoint } from 'config/api/api';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { getUserData } from 'utils';
import Loader from 'loaders/Loader';
import { toProperCase } from 'utils/formatting';

const ClinicModal = ({ isOpen, onClose, type = 'add', clinicId = null, fetchAgain }) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;
  const [loading, setLoading] = useState(false);
  const [fetchingClinic, setFetchingClinic] = useState(false);
  const [initialData, setInitialData] = useState(null);
  console.log(" ClinicModal ~ initialData:", initialData)
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirm_password: false,
  });
  const [roles, setRoles] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);

  useEffect(() => {
    if (isOpen) {
      formik.resetForm();            // Reset form to initial state
      setInitialData(null);         // Clear initial data
      setSelectedDashboards([]);    // Clear dashboard selection
    }
  }, [isOpen]);

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };


  const fetchClinic = async () => {
    try {
      setFetchingClinic(true);
      const response = await createAPIEndPoint(`clinic`).fetchById(`/${clinicId}`);
      setInitialData(response.data);
    } catch (err) {
      toast.error('Error fetching clinic data');
    }
    finally {
      setFetchingClinic(false); // ✅ hide loader
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await createAPIEndPoint(`clinic_roles/get_all/${clinic_id}`).fetchAll();
      setRoles(response.data.roles || []);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Error fetching roles');
    }
  };

  const fetchDashboards = async () => {
    try {
      const response = await createAPIEndPoint('dashboard').fetchAll();
      const dashboards = response.data.dashboards || [];
      setDashboards(dashboards.map(d => ({ id: d.id, name: d.name })));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to fetch dashboards');
    }
  };

  const handleDashboardChange = (id) => {
    setSelectedDashboards(prev =>
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
      fetchDashboards();

      if (clinicId && type === 'edit') {
        fetchClinic();  // fetchClinic sets initialData
      }
    }
  }, [clinicId, isOpen]);


  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialData?.clinic?.clinic_name || '',
      email: initialData?.user?.email?.trim() || '',
      address: initialData?.clinic?.address || '',
      city: initialData?.clinic?.city || '',
      state: initialData?.clinic?.state || '',
      phone: initialData?.clinic?.phone || '',
      // password: '',
      // confirm_password: '',
      // clinic_role_id: initialData?.user?.clinic_role_id || '',
      // designation: initialData?.designation || '',
      role_id: initialData?.role_id || 1,
      dashboard_id: initialData?.dashboard_id || '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      email: Yup.string()
        .email('Please enter a valid email address')
        .required('Email is required'),
      address: Yup.string().required('Address is required'),
      phone: Yup.string()
        .matches(/^\+1\d{10}$/, 'Phone number must be in +1XXXXXXXXXX format (e.g. +11234567890)')
        .required('Phone number is required'),
      // password: type === 'edit'
      //   ? Yup.string()
      //   : Yup.string()
      //     .matches(
      //       /^(?=.*[a-zA-Z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d]{8,}$/,
      //       'Password must be at least 8 characters long and include one uppercase letter, one lowercase letter, and one number'
      //     )
      //     .required('Password is required'),
      // confirm_password: type === 'edit'
      //   ? Yup.string()
      //   : Yup.string()
      //     .oneOf([Yup.ref('password')], 'Passwords do not match')
      //     .required('Please confirm your password'),
      // clinic_role_id: Yup.string().required('Please select a role'),
      // designation: Yup.string().required('Designation is required'),
    })
    ,
    onSubmit: async (values, { resetForm }) => {
      try {
        setLoading(true);
        const payload = { ...values, dashboard_ids: selectedDashboards };
        if (type === 'edit') {
          await createAPIEndPoint('clinic/').update(clinicId, payload);
          toast.success('Clinic updated');
        } else {
          await createAPIEndPoint('add_clinic').create(payload);
          toast.success('Clinic created');
        }
        resetForm();
        onClose();
        fetchAgain();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Something went wrong');
      } finally {
        setLoading(false);
      }
    },
  });



  return (
    <>

      <Modal isOpen={isOpen} onClose={() => {
        onClose();
        formik.resetForm();
      }}
        isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="16px" maxWidth={{ base: '90%', md: '500px' }}>
          {fetchingClinic && <Loader isAbsolute={true} />}
          <ModalHeader color="brand.500">{type === 'edit' ? 'Edit Clinic' : 'Add Clinic'}</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <ModalBody maxHeight="70vh" overflowY="auto" pb={2}>
              {/* {['name', 'email', 'address', 'phone', 'designation'].map((field, idx) => ( */}
              {['name', 'email', 'address', 'phone'].map((field, idx) => (
                <FormControl key={idx} isInvalid={formik.touched[field] && formik.errors[field]} mb={3}>
                  <FormLabel textTransform="capitalize">{field.replace('_', ' ')}</FormLabel>

                  {field === 'phone' ? (
                    <Input
                      name="phone"
                      placeholder="Enter phone number"
                      variant="auth"
                      fontSize="sm"
                      fontWeight="500"
                      value={formik.values.phone}
                      onChange={(e) => {
                        let input = e.target.value.replace(/[^\d]/g, '');
                        if (!input.startsWith('1')) input = '1' + input;
                        const formatted = '+1' + input.slice(1, 11);
                        formik.setFieldValue('phone', formatted);
                      }}
                      onBlur={formik.handleBlur}
                      maxLength={12}
                    />
                  ) : field === 'email' ? (
                    <Input
                      name="email"
                      type="email"
                      placeholder="Enter email"
                      autoComplete="off"           // ⛔ Prevent browser autofill
                      variant="auth"
                      fontSize="sm"
                      fontWeight="500"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  ) : (
                    <Input
                      name={field}
                      placeholder={`Enter ${field.replace('_', ' ')}`}
                      variant="auth"
                      fontSize="sm"
                      fontWeight="500"
                      value={formik.values[field]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  )}

                  {formik.touched[field] && formik.errors[field] && (
                    <Text color="red.500" fontSize="sm">{formik.errors[field]}</Text>
                  )}
                </FormControl>
              ))}

              <FormControl isInvalid={formik.touched.city && formik.errors.city} mb={3}>
                <FormLabel>City</FormLabel>
                <Input
                  name="city"
                  placeholder="Enter city"
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.city && formik.errors.city && (
                  <Text color="red.500" fontSize="sm">{formik.errors.city}</Text>
                )}
              </FormControl>

              <FormControl isInvalid={formik.touched.state && formik.errors.state} mb={3}>
                <FormLabel>State</FormLabel>
                <Input
                  name="state"
                  placeholder="Enter state"
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  value={formik.values.state}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.state && formik.errors.state && (
                  <Text color="red.500" fontSize="sm">{formik.errors.state}</Text>
                )}
              </FormControl>



              {/* Password */}
              {/* {type !== 'edit' &&
                ['password', 'confirm_password'].map((field) => (
                  <FormControl key={field} isInvalid={formik.touched[field] && formik.errors[field]} mb={3}>
                    <FormLabel>{field === 'confirm_password' ? 'Confirm Password' : 'Password'}</FormLabel>
                    <InputGroup>
                      <Input
                        name={field}
                        type={showPassword[field] ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder={`Enter ${field.replace('_', ' ')}`}
                        variant="auth"
                        fontSize="sm"
                        fontWeight="500"
                        value={formik.values[field]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <InputRightElement>
                        <Icon
                          as={showPassword[field] ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                          onClick={() => togglePasswordVisibility(field)}
                          cursor="pointer"
                        />
                      </InputRightElement>
                    </InputGroup>
                    {formik.touched[field] && formik.errors[field] && (
                      <Text color="red.500" fontSize="sm">{formik.errors[field]}</Text>
                    )}
                  </FormControl>
                ))
              } */}



              {/* Role Dropdown */}
              {/* <FormControl isInvalid={formik.touched.clinic_role_id && formik.errors.clinic_role_id} mb={3}>
                <FormLabel>Clinic Role</FormLabel>
                <Select
                  fontSize="sm"
                  fontWeight="500"
                  variant="auth"
                  name="clinic_role_id"
                  placeholder="Select Role"
                  value={formik.values.clinic_role_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{toProperCase(r.role_name)}</option>
                  ))}
                </Select>
                {formik.touched.clinic_role_id && formik.errors.clinic_role_id && (
                  <Text color="red.500" fontSize="sm">{formik.errors.clinic_role_id}</Text>
                )}
              </FormControl> */}

              {/* Dashboards */}
              <FormControl mb={3}>
                <FormLabel>Select Dashboards</FormLabel>
                <VStack align="start">
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                    {dashboards.map((d) => (
                      <Checkbox
                        key={d.id}
                        isChecked={selectedDashboards.includes(d.id)}
                        onChange={() => handleDashboardChange(d.id)}
                        sx={{ textTransform: "capitalize" }}
                      >
                        <span style={{ fontSize: '.85rem', textTransform: "capitalize" }}>{d.name}</span>

                      </Checkbox>
                    ))}
                  </SimpleGrid>
                </VStack>
              </FormControl>

            </ModalBody>
            <ModalFooter pt={5}>
              <Button
                isLoading={loading}
                type="submit"
                colorScheme="brand"
                w="100%"
              >
                {type === 'edit' ? 'Update Clinic' : 'Add Clinic'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ClinicModal;
